import type { ReactNode } from 'react';
import React from 'react';
import { renderToString } from 'react-dom/server';
import styled from 'styled-components';
import { RadioButton, Badge, Label } from '@lt/components';
import cn from 'classnames';

import {
  Warning as WarningIcon,
  Clock as ClockIcon,
} from '@lt/components/icons-react';
import { PaymentCards, PaymentMir } from '@lt/components/icons-react/userflow';
import {
  PAYMENT_GROUPS,
  PAYMENT_METHOD_MIR_CARD,
} from 'src/store/payment/constants';
import { formatCurrency } from 'src/utils/stringUtils';
import { useTranslation } from '@lt/localization';
import withMobile from 'checkout/CheckoutRoutes/PackageCheckout/hocs/withMobile';
import type { PaymentMethod as PaymentMethodType } from 'checkout/types/payment';
import { getPaymentGroup } from '../../../utils/orderUtils';
import AgreeBlock from '../Agreement/AgreeBlock';
import PaylatePayment from './PaylatePayment';
import CurrencyFormat from '../../../../../components/CurrencyFormat';
import SubmitOrderButtonContainer from '../SubmitOrderButton';
import CardPaymentContainer from './components/CardPaymentContainer';

import { PaymentDescription, icon as iconStyle } from './styles';

const baseCls = 'checkout-payment';

interface PaymentMethodProps {
  item: PaymentMethodType;
  isActive?: boolean;
  isDisabled?: boolean;
  isMobile?: boolean;
  onChange: (type: string) => void;
  hasMirPayment?: boolean;
}

const PaymentMethod = ({
  item,
  isActive,
  onChange,
  isMobile = false,
  hasMirPayment,
  isDisabled,
}: PaymentMethodProps): JSX.Element => {
  const { t } = useTranslation();
  const { type, description, label, alt_label, badge, amount } = item;
  const paymentGroup = getPaymentGroup(type);

  const editedDescription = isDisabled
    ? `${renderToString(<StyledWarningIcon />)}${description}`
    : `${description}`;

  return (
    <div
      className={cn(`${baseCls}__method`, {
        [`${baseCls}__method--active`]: isActive,
      })}
    >
      <div className={`${baseCls}__row`}>
        <div className={`${baseCls}__radio`}>
          <RadioButton
            name="payment"
            value={type}
            id={type}
            className="checkout-radiobutton"
            checked={isActive}
            disabled={isDisabled}
            onChange={() => onChange(type)}
          />
        </div>
        <div className={`${baseCls}__block`}>
          <label htmlFor={type}>
            <StyledPaymentLabel
              $isMobile={isMobile}
              $isActive={isActive}
              $isDisabled={isDisabled}
              $hasLabel={paymentGroup === PAYMENT_GROUPS.PAY_LATER}
            >
              <StyledLabelText $isDisabled={isDisabled}>
                {label}
                {paymentGroup === PAYMENT_GROUPS.CARD &&
                  (type.includes(PAYMENT_METHOD_MIR_CARD) ? (
                    <PaymentMirStyled />
                  ) : (
                    <PaymentCardsStyled />
                  ))}
              </StyledLabelText>

              {paymentGroup === PAYMENT_GROUPS.PAY_LATER && !isDisabled && (
                <StyledPayTimeLabel
                  iconComponent={<StyledClockIcon />}
                  title="24 часа на оплату"
                  isMobile={isMobile}
                />
              )}

              {badge && (
                <StyledBadge primary className={`${baseCls}__badge`}>
                  {badge}
                </StyledBadge>
              )}
            </StyledPaymentLabel>
          </label>
          <DescriptionWrapper type={type}>
            {type.includes(PAYMENT_METHOD_MIR_CARD) && (
              <PaymentDescription isActive={isActive} isMobile={isMobile}>
                {alt_label}
              </PaymentDescription>
            )}
            {description && (
              <PaymentDescription
                isActive={isActive}
                isDisabled
                isMobile={isMobile}
                dangerouslySetInnerHTML={{ __html: editedDescription }}
              />
            )}
          </DescriptionWrapper>
        </div>
      </div>

      {isActive && (
        <div className={`${baseCls}__paymentblock`}>
          {paymentGroup !== PAYMENT_GROUPS.PAY_LATER && (
            <div
              className={cn(`${baseCls}__left`, {
                [`${baseCls}__left__paylate`]:
                  paymentGroup === PAYMENT_GROUPS.PAYLATE,
              })}
            >
              {paymentGroup === PAYMENT_GROUPS.CARD && (
                <StyledCardPayment
                  $isMobile={isMobile}
                  showPaymentIcon={!type.includes(PAYMENT_METHOD_MIR_CARD)}
                  TooltipFooter={
                    <PaymentSelectionButton
                      href={`#${PAYMENT_METHOD_MIR_CARD}`}
                      onClick={() => onChange(PAYMENT_METHOD_MIR_CARD)}
                    >
                      Выбрать способ оплаты
                    </PaymentSelectionButton>
                  }
                  /* Если заказ можно оплатить МИР-ом, но человек случайно выбрал не МИР, маякнуть ему */
                  validateMIRCard={
                    isActive &&
                    hasMirPayment &&
                    !type.includes(PAYMENT_METHOD_MIR_CARD)
                  }
                />
              )}
              {paymentGroup === PAYMENT_GROUPS.PAYLATE && (
                <PaylatePayment item={item} />
              )}
            </div>
          )}

          <div
            className={cn(`${baseCls}__right`, {
              [`${baseCls}__right__paylate`]:
                paymentGroup === PAYMENT_GROUPS.PAYLATE,
              [`${baseCls}__right__pay_later`]:
                paymentGroup === PAYMENT_GROUPS.PAY_LATER,
            })}
          >
            <AgreeBlock />

            <StyledBottomContainer $isMobile={isMobile}>
              <SubmitOrderButtonContainer>
                {paymentGroup === PAYMENT_GROUPS.CARD ? (
                  <CurrencyFormat
                    prefix={t('checkout.payment.bookFor')}
                    value={amount}
                  />
                ) : (
                  t('checkout.payment.bookTour')
                )}
              </SubmitOrderButtonContainer>
              {paymentGroup === PAYMENT_GROUPS.PAY_LATER && (
                <StyledPayTimeBlock $isMobile={isMobile}>
                  <StyledClockIcon />
                  Оплатить{' '}
                  <StyledPrice>{formatCurrency(amount || 0)}</StyledPrice> в
                  течение 24 часов
                </StyledPayTimeBlock>
              )}
            </StyledBottomContainer>
          </div>
        </div>
      )}
    </div>
  );
};

const StyledPrice = styled.div`
  font-weight: bold;
  margin: 0 4px;
`;

const StyledPaymentLabel = styled.h3<{
  $isMobile?: boolean;
  $hasLabel?: boolean;
  $isDisabled?: boolean;
  $isActive?: boolean;
}>`
  margin-top: ${({ $isMobile }) => ($isMobile ? '0' : '-2px')};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  font-size: ${({ $isMobile }) => ($isMobile ? '13px' : '18px')};
  font-weight: normal;
  color: ${({ theme }) => theme.colors.blackBase};
  cursor: pointer;
  user-select: none;
  ${({ $hasLabel, $isMobile }) =>
    $hasLabel && $isMobile && `flex-direction: column;`}
  ${({ $isActive, $isMobile }) =>
    ($isActive || $isMobile) && ' font-weight: bold;'}
  ${({ $isDisabled, theme }) =>
    $isDisabled && `color: ${theme.colors.greyLight}`}
  ${({ $isMobile }) => $isMobile && 'align-items: flex-start;'}
`;

const StyledLabelText = styled.div<{ $isDisabled?: boolean }>`
  ${({ $isDisabled, theme }) =>
    $isDisabled && `color: ${theme.colors.greyLight}`}
  > i {
    position: relative;
    margin-left: 7px;
    flex-shrink: 0;
    margin-top: 1px;
    height: 100%;
    vertical-align: top;
  }
`;

const StyledWarningIcon = styled(WarningIcon).attrs({ viewBox: '0 0 13 13' })`
  margin-right: 2px;
  vertical-align: text-bottom;
  width: 16px;
  height: 16px;
`;

const StyledPayTimeLabel = styled(Label).attrs({ style: 7 })<{
  isMobile: boolean;
}>`
  margin: 4px 0 0 ${({ isMobile }) => (isMobile ? '0' : '7px')};
  font-weight: normal;
`;

const StyledClockIcon = styled(ClockIcon)`
  width: 16px;
  height: 16px;

  path {
    stroke: black;
  }
  margin-right: 6px;
`;

const StyledBottomContainer = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  align-items: ${({ $isMobile }) => ($isMobile ? 'flex-start' : 'center')};
`;

const StyledPayTimeBlock = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  align-items: center;
  margin-left: ${({ $isMobile }) => ($isMobile ? '0px' : '18px')};
  ${({ $isMobile }) => $isMobile && 'margin-top: 12px'};
`;

const PaymentCardsStyled = styled(PaymentCards)`
  width: 95px;
  ${iconStyle}
`;

const PaymentMirStyled = styled(PaymentMir)`
  width: 34px;
  ${iconStyle}
`;

const StyledCardPayment = styled(CardPaymentContainer)<{ $isMobile?: boolean }>`
  ${({ $isMobile }) => $isMobile && 'margin-top: 14px;'}
`;

const PaymentSelectionButton = styled.a`
  text-decoration: none;
  color: #007aff;
  display: block;
  text-align: right;
`;

interface DescriptionWrapperProps {
  type: string;
  children: ReactNode;
  isMobile?: boolean;
}

const DescriptionWrapper = withMobile(
  ({ type, children, isMobile }: DescriptionWrapperProps) =>
    React.createElement(
      isMobile ? 'label' : React.Fragment,
      isMobile ? { htmlFor: type } : null,
      children,
    ),
);

const StyledBadge = styled(Badge)`
  font-size: 14px;
  padding: 2px 6px 3px;
`;

export default withMobile(PaymentMethod);
